import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PlayCircle, Eye, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { emrService, EmrAppointment } from '@/services/emrService';
import api from '@/services/api';
import { visitCockpitPath } from '@/lib/portalRoutes';
import { parseApiError } from '@/lib/apiErrors';

/**
 * Single-click "Start / Resume / Open Visit" action.
 *
 * The same button surfaces from the calendar, AppointmentSheet, the
 * patient chart Appointments tab, and the patient-overview Upcoming
 * Visits panel. The label and behavior derive purely from
 * `appointment.status`:
 *
 *   scheduled | confirmed | checked_in   → "Start Visit"   (POST start-visit)
 *   in_progress                          → "Resume Visit"  (look up encounter, navigate)
 *   completed                            → "Open Visit"    (look up encounter, navigate read-only)
 *   cancelled | no_show                  → render nothing
 *
 * The backend's start-visit endpoint is idempotent — calling it on an
 * appointment that's already in_progress returns the existing encounter
 * (so a stale FE cache can't double-create).
 */

interface StartVisitResponse {
  encounter: { id: string };
  appointment: { id: string; status: string };
}

interface VisitActionButtonProps {
  appointment: Pick<EmrAppointment, 'id' | 'status' | 'patient_id'> & Partial<EmrAppointment>;
  /**
   * Visual variant. "primary" = filled brand-teal (header use);
   * "outline" = subdued (in-row use); "icon" = compact play-icon-only.
   */
  variant?: 'primary' | 'outline' | 'icon';
  /** Override the default size. */
  size?: 'sm' | 'default' | 'lg';
  /** Stop click propagation (useful when nested inside a clickable row). */
  stopPropagation?: boolean;
  className?: string;
  /** Called after navigation kicks off (useful for closing parent dialogs). */
  onLaunched?: (encounterId: string) => void;
}

type Action = 'start' | 'resume' | 'open' | null;

const ACTION_LABEL: Record<NonNullable<Action>, string> = {
  start: 'Start Visit',
  resume: 'Resume Visit',
  open: 'Open Visit',
};

const ACTION_ICON: Record<NonNullable<Action>, React.ElementType> = {
  start: PlayCircle,
  resume: RefreshCw,
  open: Eye,
};

const decideAction = (status: string | undefined): Action => {
  switch (status) {
    case 'scheduled':
    case 'confirmed':
    case 'checked_in':
      return 'start';
    case 'in_progress':
      return 'resume';
    case 'completed':
      return 'open';
    case 'cancelled':
    case 'no_show':
    case 'rescheduled':
    default:
      return null;
  }
};

const VisitActionButton: React.FC<VisitActionButtonProps> = ({
  appointment, variant = 'outline', size = 'sm', stopPropagation, className, onLaunched,
}) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { clinic } = useClinic();
  const [busy, setBusy] = useState(false);

  const action = decideAction(appointment.status);
  if (!action) return null;

  const Icon = ACTION_ICON[action];
  const label = ACTION_LABEL[action];

  /**
   * Look up the encounter for this appointment. Used by 'resume' and
   * 'open' where the encounter already exists. Returns the first hit.
   */
  const findEncounter = async (): Promise<string | null> => {
    if (!clinic?.id) return null;
    const encounters = await emrService.getEncounters(clinic.id, {
      patientId: appointment.patient_id,
    });
    // Prefer the one explicitly linked to this appointment.
    const exact = encounters.find(e => e.appointment_id === appointment.id);
    if (exact) return exact.id;
    // Fallback: most recent encounter for this patient (defensive — shouldn't happen
    // if the start-visit orchestration ran).
    return encounters[0]?.id ?? null;
  };

  const handleClick = async (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    if (!clinic?.id) {
      toast({ title: 'Clinic context unavailable', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      let encounterId: string | null = null;

      if (action === 'start') {
        // Single backend call: transitions status (scheduled|confirmed|checked_in
        // → in_progress) and creates/reuses the encounter atomically.
        const response = await api.post(
          `/emr/${clinic.id}/appointments/${appointment.id}/start-visit`,
          {},
        );
        const payload: StartVisitResponse = response.data?.data || response.data;
        encounterId = payload?.encounter?.id || null;
        // Invalidate appointment lists so the status badge updates everywhere.
        qc.invalidateQueries({ queryKey: ['appointment'] });
        qc.invalidateQueries({ queryKey: ['patient', appointment.patient_id, 'appointments'] });
      } else {
        encounterId = await findEncounter();
      }

      if (!encounterId) {
        toast({
          title: 'No encounter found',
          description: 'The visit record could not be located. Try again or open the appointment.',
          variant: 'destructive',
        });
        return;
      }

      onLaunched?.(encounterId);
      navigate(visitCockpitPath(encounterId));
    } catch (err) {
      const e = parseApiError(err);
      toast({
        title: action === 'start' ? 'Could not start visit' : 'Could not open visit',
        description: e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        type='button'
        onClick={handleClick}
        disabled={busy}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-teal text-white shadow-sm hover:bg-brand-teal/90 disabled:opacity-60 ${className || ''}`}
        title={label}
        aria-label={label}
      >
        {busy ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Icon className='h-3.5 w-3.5' />}
      </button>
    );
  }

  const buttonVariant = variant === 'primary' ? 'default' : 'outline';
  const colorClass = variant === 'primary'
    ? 'bg-brand-teal text-white hover:bg-brand-teal/90'
    : action === 'open'
      ? ''
      : 'border-brand-teal/30 text-brand-teal hover:bg-brand-teal/5';

  return (
    <Button
      type='button'
      variant={buttonVariant}
      size={size}
      onClick={handleClick}
      disabled={busy}
      className={`${colorClass} ${className || ''}`}
    >
      {busy ? (
        <>
          <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
          {action === 'start' ? 'Starting…' : 'Loading…'}
        </>
      ) : (
        <>
          <Icon className='mr-1.5 h-3.5 w-3.5' />
          {label}
        </>
      )}
    </Button>
  );
};

export default VisitActionButton;
